import type { Brand as MelwaysBrand } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { useMemo } from 'react';

import { createSuggestion } from 'src/pages/contact-us/App/components/EnquiryForm/helpers/suggestionHelper';
import type { UrlWithPlaceholder } from 'src/pages/contact-us/App/components/EnquiryForm/helpers/suggestionMapping';
import { BRAND } from 'src/shared/constant';
import { useConfig } from 'src/shared/hooks';
import type { SuggestionType } from 'src/shared/types';
import { getCorrectlyCasedBrandName } from 'src/utils/brandUtils';

import translations from './.vocab';

// Define a local Brand type that includes seekAnz
type Brand = MelwaysBrand | 'seekAnz';

export interface SubItem {
  type: string;
  name: string;
  label: string;
  required: boolean;
  options?: Array<{
    name: string;
    label: string;
    value?: string;
  }>;
}

export interface Reason {
  reason: string;
  rspToggleRequired?: boolean;
  brand?: Brand[];
  suggestion?: SuggestionType;
  subItems?: SubItem[];
  placeholder?: UrlWithPlaceholder;
}

const signIn = {
  [BRAND.seek]: 'Sign in / Password',
  [BRAND.jobsdb]: 'Login / Password',
  [BRAND.jobstreet]: 'Login / Password',
};

const jobMail = {
  [BRAND.seek]: 'JobMail',
  [BRAND.jobsdb]: 'Job recommendation emails',
  [BRAND.jobstreet]: 'Job recommendation emails',
};

export type Subject = Record<string, Reason[]>;

// Helper function to get the correct profile subject key based on brand
const getProfileSubjectKey = (brand: Brand): string => {
  switch (brand) {
    case 'seekAnz':
    case 'seek':
      return 'SEEK Profile';
    default:
      return 'Jobstreet Profile';
  }
};

// Helper function to get the correct profile reason key based on brand
const getProfileReasonKey = (brand: Brand): string => {
  switch (brand) {
    case 'seekAnz':
    case 'seek':
      return 'What is a SEEK Profile';
    default:
      return 'What is a Jobstreet Profile';
  }
};

export const useSubjects = (brand: Brand) => {
  const { t } = useTranslations(translations);
  const { country } = useConfig();
  const countryCode = country.toUpperCase();

  const subjectData: Subject = useMemo(() => {
    const profileSubjectKey = getProfileSubjectKey(brand);
    const profileReasonKey = getProfileReasonKey(brand);

    // Use the translated version for display, but the explicit key for logic
    const correctlyCasedBrand = getCorrectlyCasedBrandName(
      brand as MelwaysBrand,
    );
    const profileSubjectDisplay = t('{brandName} Profile', {
      brandName: correctlyCasedBrand,
    });
    const profileReasonDisplay = t('What is a {brandName} Profile', {
      brandName: correctlyCasedBrand,
    });

    const data = {
      // Keep other subjects as they were, using t()
      [t(signIn[brand] as 'Sign in / Password' | 'Login / Password')]: [
        {
          reason: t('Forgot my username or password'),
          suggestion: createSuggestion(
            'Login / Password',
            'Forgot my username or password',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Sign in error'),
          suggestion: createSuggestion(
            'Login / Password',
            'Sign in error',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('My account has been blocked'),
          suggestion: createSuggestion(
            'Login / Password',
            'My account has been blocked',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('I didn’t receive my password reset e-mail'),
          suggestion: createSuggestion(
            'Login / Password',
            'I didn’t receive my password reset e-mail',
            countryCode,
            brand,
          ),
        },
        { reason: t('Other') },
      ],
      [t('My Account')]: [
        {
          reason: t('Update account details'),
          suggestion: createSuggestion(
            'My Account',
            'Update account details',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Update my e-mail address'),
          suggestion: createSuggestion(
            'My Account',
            'Update my e-mail address',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Registration'),
          suggestion: createSuggestion(
            'My Account',
            'Registration',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Applied Jobs'),
          suggestion: createSuggestion(
            'My Account',
            'Applied jobs',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Saved jobs'),
          suggestion: createSuggestion(
            'My Account',
            'Saved jobs',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Delete account'),
          suggestion: createSuggestion(
            'My Account',
            'Delete account',
            countryCode,
            brand,
          ),
        },
        { reason: t('Other') },
      ],
      [t('Job Search')]: [
        brand === BRAND.seek && {
          reason: t('How does {brandName}’s search engine work?', {
            brandName: getCorrectlyCasedBrandName(brand as MelwaysBrand),
          }),
          brand: [brand],
        },
        {
          reason: t('How to search for a job'),
          suggestion: createSuggestion(
            'Job Search',
            'How to search for a job',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Job search issues/error'),
          suggestion: createSuggestion(
            'Job Search',
            'Job search issues/error',
            countryCode,
            brand,
          ),
        },
        brand === BRAND.seek && { reason: t('Location search') },
        brand === BRAND.seek && { reason: t('Salary') },
        { reason: t('Other') },
      ],
      [t('Applying for a job')]: [
        {
          reason: t('Application error'),
          suggestion: createSuggestion(
            'Applying for a job',
            'Application error',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('How to apply'),
          suggestion: createSuggestion(
            'Applying for a job',
            'How to apply',
            countryCode,
            brand,
          ),
        },
        brand === BRAND.seek && { reason: t('Certsy') },
        {
          reason: t('Privacy & data protection'),
          suggestion: createSuggestion(
            'Applying for a job',
            'Privacy & data protection',
            countryCode,
            brand,
          ),
        },
        brand === BRAND.seek && {
          reason: t('International application / Visa queries'),
        },
        {
          reason: t('Cover letter'),
          suggestion: createSuggestion(
            'Applying for a job',
            'Cover letter',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Withdrawal of application'),
          suggestion: createSuggestion(
            'Applying for a job',
            'Withdrawal of application',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Follow up application'),
          suggestion: createSuggestion(
            'Applying for a job',
            'Follow up application',
            countryCode,
            brand,
          ),
        },
        { reason: t('Other') },
      ],
      // Use the display name for the subject key in the form
      [profileSubjectDisplay]: [
        {
          // Use the display name for the reason dropdown
          reason: profileReasonDisplay,
          // Use the explicit keys when calling createSuggestion
          suggestion: createSuggestion(
            profileSubjectKey,
            profileReasonKey,
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Create or Upload a resumé'),
          suggestion: createSuggestion(
            profileSubjectKey,
            'Create or Upload a resumé',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Edit or Delete a resumé'),
          suggestion: createSuggestion(
            profileSubjectKey,
            'Edit or Delete a resumé',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Update my profile'),
          suggestion: createSuggestion(
            profileSubjectKey,
            'Update my profile',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('My profile visibility'),
          suggestion: createSuggestion(
            profileSubjectKey,
            'My profile visibility',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Delete my profile'),
          suggestion: createSuggestion(
            profileSubjectKey,
            'Delete my profile',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Profile sent with application'),
          suggestion: createSuggestion(
            profileSubjectKey,
            'Profile sent with application',
            countryCode,
            brand,
          ),
        },
        { reason: t('Other') },
      ],
      [t('Report a suspicious ad')]: [
        {
          reason: t('Report a suspicious ad'),
          suggestion: createSuggestion(
            'Report a suspicious ad',
            'Report a suspicious ad',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Report a suspicious hirer'),
          suggestion: createSuggestion(
            'Report a suspicious ad',
            'Report a suspicious hirer',
            countryCode,
            brand,
          ),
        },
      ],
      [t('Email Issues')]: [
        {
          reason: t('I’ve stopped receiving e-mails'),
          suggestion: createSuggestion(
            'Email Issues',
            'I’ve stopped receiving e-mails',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('I need to update my e-mail address'),
          suggestion: createSuggestion(
            'Email Issues',
            'I need to update my e-mail address',
            countryCode,
            brand,
          ),
        },
        {
          reason: t('Unsubscribe from emails'),
          suggestion: createSuggestion(
            'Email Issues',
            'Unsubscribe from emails',
            countryCode,
            brand,
          ),
        },
        {
          reason: t(jobMail[brand] as 'JobMail' | 'Job recommendation emails'),
          suggestion: createSuggestion(
            'Email Issues',
            jobMail[brand],
            countryCode,
            brand,
          ),
        },
        brand === BRAND.seek && { reason: t('Weekly Round-up') },
        { reason: t('Other') },
      ],
      [t('Other')]: [
        { reason: t('General feedback') },
        { reason: t('Product feedback') },
        { reason: t('Company Reviews') },
      ],
    };

    // eslint-disable-next-line guard-for-in
    for (const subject in data) {
      data[subject] = data[subject].filter((reason) => reason);
    }

    return data as Subject;
  }, [brand, countryCode, t]);

  return { subjectData };
};
